import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Timer, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DURATION = 60;

export default function MentalMaths() {
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [val, setVal] = useState("");
  const [correct, setCorrect] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [state, setState] = useState("idle"); // idle | running | done
  const [wrong, setWrong] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const { user } = useAuth();

  const start = async () => {
    const { data } = await api.get("/mental-maths/session");
    setSession(data);
    setIdx(0); setVal(""); setCorrect(0); setTimeLeft(DURATION); setState("running");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (state !== "running") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timerRef.current); finish(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const finish = async () => {
    setState("done");
    if (user && session) {
      try {
        await api.post("/mental-maths/submit", {
          correct, total: session.questions.length, seconds: DURATION - timeLeft,
        });
      } catch {}
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!session || state !== "running") return;
    const q = session.questions[idx];
    if (parseInt(val, 10) === q.answer) {
      setCorrect((c) => c + 1);
      const nextIdx = idx + 1;
      if (nextIdx >= session.questions.length) { finish(); return; }
      setIdx(nextIdx); setVal("");
    } else {
      setWrong(true); setTimeout(() => setWrong(false), 400);
    }
  };

  const progress = session ? ((idx) / session.questions.length) * 100 : 0;
  const timePct = (timeLeft / DURATION) * 100;

  return (
    <div className="max-w-xl mx-auto">
      <div className="mb-6 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Rapid-fire · 60s</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Mental Maths</h1>
        <p className="text-zinc-400 text-sm mt-2">Answer as many as you can. Wrong answer? Try again — the clock ticks on.</p>
      </div>

      {state === "idle" && (
        <div className="rounded-lg border border-white/10 bg-[#152040] p-8 text-center">
          <Button data-testid="mental-start-btn" onClick={start} size="lg" className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold">Start sprint</Button>
        </div>
      )}

      {state === "running" && session && (
        <div className={`rounded-lg border border-white/10 bg-[#152040] p-8 ${wrong ? "shake" : ""}`} data-testid="mental-card">
          <div className="flex justify-between items-center text-xs text-zinc-500 mb-2">
            <span><Timer className="w-3.5 h-3.5 inline mr-1" />{timeLeft}s</span>
            <span className="font-mono text-yellow-300">{correct} correct</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-6">
            <div className="h-full bg-yellow-400 transition-[width] duration-1000 ease-linear" style={{ width: `${timePct}%` }} />
          </div>
          <div className="text-center mb-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Question {idx + 1}</div>
            <div className="font-mono text-5xl font-bold text-pink-300" data-testid="mental-question">{session.questions[idx].q}</div>
          </div>
          <form onSubmit={submit}>
            <Input
              ref={inputRef}
              data-testid="mental-answer-input"
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="bg-[#0b1428] border-white/10 focus:border-cyan-400 text-center text-2xl font-mono h-14"
              placeholder="?"
              autoFocus
            />
            <Button type="submit" data-testid="mental-submit-btn" className="w-full mt-4 bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold">
              Enter
            </Button>
          </form>
          <div className="mt-2 text-[11px] text-zinc-600 text-center">Tip: hit ENTER to submit fast</div>
        </div>
      )}

      {state === "done" && (
        <div className="rounded-lg border border-white/10 bg-[#152040] p-8 text-center fade-up" data-testid="mental-result">
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Sprint over</div>
          <div className="font-display text-6xl font-bold gradient-streak mb-2">{correct}</div>
          <div className="text-zinc-400 mb-8">questions cracked in 60 seconds</div>
          <Button onClick={start} data-testid="mental-restart-btn" className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold">
            Go again
          </Button>
        </div>
      )}
    </div>
  );
}
